import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';

import { actions as promotionalVideoActions } from '../../redux/modules/promotionalVideo';
import * as promotionalVideoSelectors from '../../redux/selectors/promotionalVideo';

import {
  buildAutoplayEmbedUrl,
  getVideoProvider,
  getVideoThumbnail,
  isSafeHttpUrl,
} from '../../utils/videoEmbed';

import { Container, VideoWrapper, Thumbnail, CloseButton, CtaButton } from './styles';

const SESSION_DISMISSED_KEY = 'promotional-video-popup-dismissed';

const wasDismissedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const markDismissedThisSession = (): void => {
  try {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
  } catch {
    // sessionStorage indisponível (ex.: modo privado) — apenas ignora a persistência.
  }
};

/**
 * Popup flutuante de vídeo promocional exibido no canto da tela.
 * - Busca o vídeo ativo (sorteado no backend) uma vez por sessão.
 * - Reproduz automaticamente e mutado, com thumbnail/loading enquanto carrega.
 * - Fecha de forma discreta e não reaparece na mesma sessão.
 */
const PromotionalVideoPopup = () => {
  const dispatch = useDispatch();
  const video = useSelector(promotionalVideoSelectors.getPromotionalVideo);

  const [isDismissed, setIsDismissed] = useState<boolean>(() => wasDismissedThisSession());
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    // Busca o vídeo apenas se ainda não estiver em cache no store e o popup
    // não tiver sido dispensado nesta sessão; evita re-fetch a cada montagem.
    if (!isDismissed && !video) {
      dispatch(promotionalVideoActions.getPromotionalVideo());
    }
    // Dispara apenas na montagem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    markDismissedThisSession();
    setIsDismissed(true);
  };

  // Renderiza o iframe apenas para provedores conhecidos (YouTube/Vimeo),
  // evitando carregar URLs arbitrárias caso o Backoffice envie um embed inválido.
  if (isDismissed || !video || !video.embed_url || getVideoProvider(video.embed_url) === 'unknown') {
    return null;
  }

  const embedSrc = buildAutoplayEmbedUrl(video.embed_url);
  const thumbnail = getVideoThumbnail(video.embed_url);
  const ctaUrl = video.cta_url;
  const hasCta = !!video.cta_text && isSafeHttpUrl(ctaUrl);

  return (
    <Container role="complementary" aria-label="Vídeo promocional" data-testid="promotional-video-popup">
      <VideoWrapper>
        <CloseButton
          type="button"
          onClick={handleClose}
          aria-label="Fechar vídeo promocional"
          data-testid="promotional-video-close"
        >
          <CloseOutlined />
        </CloseButton>

        {!isVideoLoaded && (
          <Thumbnail image={thumbnail} aria-hidden="true" data-testid="promotional-video-thumbnail">
            {!thumbnail && <Spin indicator={<LoadingOutlined spin />} />}
          </Thumbnail>
        )}

        <iframe
          src={embedSrc}
          title={video.cta_text || 'Vídeo promocional'}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setIsVideoLoaded(true)}
          onError={() => setIsVideoLoaded(true)}
        />
      </VideoWrapper>

      {hasCta && (
        <CtaButton
          href={ctaUrl as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="promotional-video-cta"
        >
          {video.cta_text}
        </CtaButton>
      )}
    </Container>
  );
};

export default PromotionalVideoPopup;
